/** Shared by Labs' browser analytics adapters. No admin credential or email lookup. */
export type SubscriptionState = 'active' | 'none' | 'unknown';
export type GetToken = () => Promise<string | null>;
export type Properties = Record<string, string | number | boolean | string[]>;
export interface Activity { name: string; properties: Properties }

export const SUBSCRIPTION_QUERY = `mutation PreviewSubscriptionStatus {
  login { id email subscriptions { status createdAt expiresAt } }
}`;

export function sgappUrl(hostname: string): string | null {
  // Production Labs hosts and their earlier aliases share the normal sgapp service.
  // `collab.labs.240.org` is listed once and nowhere else: Collab is production-only, so it
  // has no development host to add. Adding a local one would break the rule below.
  if (['ahead.labs.240.org', 'ahead.preview.240.org', 'labs.240.org', 'objectives.labs.240.org', 'rubric.labs.240.org',
    'collab.labs.240.org', 'planner.labs.240.org', 'jobs.labs.240.org',
    'preview.240.org', 'objectives.preview.240.org', 'rubric.preview.240.org',
    'teacherlab.240.org', 'objectives.teacherlab.240.org', 'rubric.teacherlab.240.org'].includes(hostname)) return 'https://api.240tutoring.com/sgapp/v2';
  if (['240-preview-one-step-ahead-preview.240-tutoring.workers.dev', 'labs.dev.240.org', 'preview.dev.240.org', 'homeroom-objectives-preview.240-tutoring.workers.dev', '240-preview-rubric-preview.240-tutoring.workers.dev'].includes(hostname)) {
    return 'https://api.240tutoring.com/sgapp-dev-crq';
  }
  // Unknown hosts (including local development) never send a token to production.
  return null;
}

/** Mirrors sgapp's isActiveSubscription: active, trialing, or canceled but not expired. */
export function subscriptionState(subscriptions: unknown, at: number): SubscriptionState {
  if (!Array.isArray(subscriptions) || !Number.isFinite(at)) return 'unknown';
  let unknown = false;
  for (const item of subscriptions) {
    if (!item || typeof item !== 'object' || typeof item.status !== 'string') {
      unknown = true;
      continue;
    }
    if (!['active', 'trialing', 'canceled', 'inactive', 'unpaid', 'past_due',
      'incomplete', 'incomplete_expired', 'paused'].includes(item.status)) {
      unknown = true;
      continue;
    }
    if (item.createdAt != null && (!Number.isFinite(Date.parse(item.createdAt)) || Date.parse(item.createdAt) > at)) {
      unknown = true;
      continue;
    }
    const expires = item.expiresAt == null ? null : Date.parse(item.expiresAt);
    if (expires !== null && !Number.isFinite(expires)) { unknown = true; continue; }
    // sgapp uses isAfter(now, expiresAt): equality is still active.
    if (expires !== null && at > expires) continue;
    if (['active', 'trialing', 'canceled'].includes(item.status)) return 'active';
  }
  return unknown ? 'unknown' : 'none';
}

export function growthActivity(name: string): 'visit' | 'tool_use' | null {
  if (['preview_osa_page_viewed', 'landing_page_viewed', 'preview_objectives_page_viewed', 'objectives_page_viewed', 'rubric_page_viewed', 'preview_rubric_page_viewed', 'preview_planner_page_viewed',
    // Jobs and Collab visits, added 2026-10-15 so a teacher who only uses them counts in MAU.
    'jobs_board_viewed', 'preview_collab_page_viewed'].includes(name)) return 'visit';
  // `preview_collab_material_shared` is Collab's one qualifying action, and it is emitted
  // from the browser on purpose: this function is consulted only inside the browser-side
  // `track` below, so a server-emitted copy would never reach the growth metric (KTD15).
  // `preview_planner_weekly_viewed` is the Planner's return loop, sent from the browser for
  // the same reason (KTD12).
  if (['preview_osa_report_viewed', 'preview_osa_topic_opened', 'preview_objectives_run_completed', 'preview_objectives_report_viewed', 'objectives_report_viewed', 'rubric_scored', 'preview_rubric_scored', 'preview_collab_material_shared', 'preview_planner_weekly_viewed',
    // Opening a posting is the Jobs board's one qualifying action.
    'jobs_posting_opened'].includes(name)) return 'tool_use';
  return null;
}

export function snapshotProperties(body: unknown, at: number): Properties {
  const unknown: Properties = { subscription_state_at_event: 'unknown', subscription_schema_version: 1 };
  if (!body || typeof body !== 'object') return unknown;
  const response = body as { errors?: unknown[]; data?: { login?: { id?: unknown; email?: unknown; subscriptions?: unknown } } };
  const user = response.data?.login;
  if (response.errors?.length || !user || typeof user.id !== 'string' || !user.id) return unknown;
  const state = subscriptionState(user.subscriptions, at);
  const result: Properties = { ...unknown, subscription_state_at_event: state, sgapp_user_id: user.id };
  if (state !== 'unknown') result.has_active_240_subscription = state === 'active';
  // Missing email cannot establish that someone is not staff.
  if (typeof user.email === 'string' && user.email.includes('@')) {
    result.actor_is_staff = /@(240tutoring|240certification)\.com$/i.test(user.email.trim());
  }
  return result;
}

/**
 * Fresh lookup for each qualifying action, bounded to four seconds. No persisted status:
 * a purchase in another tab must not leave a cached `none` on future events.
 * The original event timestamp is retained; checked_at records the lookup's time.
 * Account changes cancel pending work so it cannot attach to the next PostHog person.
 */
export function createSubscriptionEnricher(options: {
  hostname: string;
  fetch?: typeof fetch;
  now?: () => number;
  timeoutMs?: number;
  /** Opt in when every product action needs subscription attribution. */
  enrichAll?: boolean;
}) {
  const request = options.fetch ?? globalThis.fetch;
  const now = options.now ?? Date.now;
  let identity: { id: string; getToken: GetToken } | null = null;
  let generation = 0;
  const pending = new Set<AbortController>();
  function setIdentity(id: string | null, getToken?: GetToken) {
    const next = id && getToken ? { id, getToken } : null;
    if ((identity?.id ?? null) !== (next?.id ?? null)) {
      generation++;
      for (const controller of pending) controller.abort();
      pending.clear();
    }
    identity = next;
  }
  function track(event: Activity, send: (event: Activity, timestamp?: Date) => void): void {
    const kind = growthActivity(event.name);
    if (!kind && !options.enrichAll) { send(event); return; }
    const timestamp = new Date(now());
    const epoch = generation;
    const subject = identity;
    const url = sgappUrl(options.hostname);
    const base = { ...event.properties, ...(kind ? { preview_activity: kind } : {}), subscription_state_at_event: 'unknown', subscription_schema_version: 1 };
    if (!subject || !url) { send({ ...event, properties: base }, timestamp); return; }
    const controller = new AbortController();
    pending.add(controller);
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 4000);
    const aborted = new Promise<Properties>(resolve => {
      controller.signal.addEventListener('abort', () => resolve({}), { once: true });
    });
    const lookup = async (): Promise<Properties> => {
      try {
        const token = await subject.getToken();
        if (!token || controller.signal.aborted || epoch !== generation) return {};
        const response = await request(url, {
          method: 'POST', credentials: 'omit', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ query: SUBSCRIPTION_QUERY }),
        });
        if (!response.ok) return {};
        const body: unknown = await response.json();
        const checkedAt = now();
        return { ...snapshotProperties(body, timestamp.getTime()),
          subscription_checked_at: new Date(checkedAt).toISOString(),
          subscription_source: 'sgapp_graphql', subscription_lookup_ms: checkedAt - timestamp.getTime() };
      } catch { return {}; }
    };
    void Promise.race([lookup(), aborted]).then(properties => {
      if (epoch === generation) send({ ...event, properties: { ...base, ...properties } }, timestamp);
    }).catch(() => { /* Analytics must never break the product. */ }).finally(() => {
      clearTimeout(timeout);
      pending.delete(controller);
    });
  }
  return { setIdentity, track, dispose: () => setIdentity(null) };
}
